import type { RidesAdapter } from '../interface'
import type { Ride, RideRequest, CreateRidePayload, SearchRideParams } from '../../../types/ride'

const ridesStore: Ride[] = [
  {
    id: 'mock-ride-1', driverId: 'mock-user-2', driverName: 'Иван П.',
    origin: { lat: 55.7558, lng: 37.6176 }, dest: { lat: 55.9647, lng: 37.8491 },
    originName: 'Москва', destName: 'Пушкино',
    departsAt: new Date(Date.now() + 3600_000).toISOString(),
    seatsTotal: 3, seatsFree: 2, status: 'active', createdAt: new Date().toISOString(),
  },
]

const requestsStore: RideRequest[] = []
const subscribers: Array<(rides: Ride[]) => void> = []

function notifySubscribers(): void {
  const active = ridesStore.filter((r) => r.status === 'active')
  subscribers.forEach((fn) => fn(active))
}

function delay(ms: number): Promise<void> { return new Promise((res) => setTimeout(res, ms)) }

export const mockRidesAdapter: RidesAdapter = {
  async searchRides(params: SearchRideParams): Promise<Ride[]> {
    await delay(200)
    return ridesStore.filter((r) => {
      if (r.status !== 'active') return false
      if (params.seatsNeeded && r.seatsFree < params.seatsNeeded) return false
      if (params.originName && !r.originName.toLowerCase().includes(params.originName.toLowerCase())) return false
      if (params.destName && !r.destName.toLowerCase().includes(params.destName.toLowerCase())) return false
      return true
    })
  },
  async createRide(payload: CreateRidePayload): Promise<Ride> {
    await delay(300)
    const stored = localStorage.getItem('mock_user_profile')
    const user = stored ? JSON.parse(stored) : { id: 'anon', displayName: 'Водитель' }
    const ride: Ride = {
      id: `ride_${Date.now()}`, driverId: user.id, driverName: user.displayName,
      origin: payload.origin, dest: payload.dest, originName: payload.originName, destName: payload.destName,
      departsAt: payload.departsAt, seatsTotal: payload.seatsTotal, seatsFree: payload.seatsTotal,
      status: 'active', createdAt: new Date().toISOString(),
    }
    ridesStore.push(ride)
    notifySubscribers()
    return ride
  },
  async cancelRide(rideId: string): Promise<void> {
    await delay(200)
    const ride = ridesStore.find((r) => r.id === rideId)
    if (ride) ride.status = 'cancelled'
    notifySubscribers()
  },
  async requestSeat(rideId: string, seatsCount: number): Promise<RideRequest> {
    await delay(200)
    const stored = localStorage.getItem('mock_user_profile')
    const user = stored ? JSON.parse(stored) : { id: 'anon' }
    const req: RideRequest = {
      id: `req_${Date.now()}`, rideId, passengerId: user.id, seatsCount,
      status: 'pending', createdAt: new Date().toISOString(),
    }
    requestsStore.push(req)
    return req
  },
  async updateRequest(requestId: string, status: 'accepted' | 'rejected'): Promise<void> {
    await delay(200)
    const req = requestsStore.find((r) => r.id === requestId)
    if (!req) return
    req.status = status
    if (status === 'accepted') {
      const ride = ridesStore.find((r) => r.id === req.rideId)
      if (ride) ride.seatsFree = Math.max(0, ride.seatsFree - req.seatsCount)
    }
  },
  async getMyRidesAsDriver(): Promise<Ride[]> {
    await delay(100)
    const stored = localStorage.getItem('mock_user_profile')
    if (!stored) return []
    const user = JSON.parse(stored)
    return ridesStore.filter((r) => r.driverId === user.id)
  },
  async getMyRidesAsPassenger(): Promise<RideRequest[]> {
    await delay(100)
    const stored = localStorage.getItem('mock_user_profile')
    if (!stored) return []
    const user = JSON.parse(stored)
    return requestsStore.filter((r) => r.passengerId === user.id)
  },
  subscribeToRides(onUpdate: (rides: Ride[]) => void): () => void {
    subscribers.push(onUpdate)
    onUpdate(ridesStore.filter((r) => r.status === 'active'))
    return () => {
      const idx = subscribers.indexOf(onUpdate)
      if (idx !== -1) subscribers.splice(idx, 1)
    }
  },
}
